import { ref, reactive, computed, watch, onBeforeUnmount, type StyleValue } from 'vue';
import { pushOverlay } from './overlayEscStack';

/**
 * usePanelControls — 弹出层通用能力：自由拖拽 / 自由缩放(宽高) / 自动吸附 / 关闭 / 最大化 / 键盘微调 / 位置持久化
 *
 * 供 UFloatingPanel 基件与各视图浮动面板（虚空池 / 技能槽 / 属性面板等）统一使用，
 * 消除各弹出层"不能拖动、不能缩放、无关闭入口"的体验断层。
 *
 * 相对 v1（零消费者旧版）的关键强化：
 * - Pointer Events + setPointerCapture：鼠标/触摸/笔统一，不再挂 window 监听（旧版残留泄漏风险）
 * - 拖拽经 transform: translate3d（合成层），缩放才改宽高（真实重排），并移除旧版
 *   transform:scale 伪缩放（内容模糊 + 命中区域错位）
 * - 视口钳制：拖拽保证 ≥48px 把手留在视口内，缩放不超视口 95%，杜绝面板被拖出屏幕"丢失"
 * - 键盘可达：焦点在标题栏时方向键移动（Shift=1px 精调），Ctrl+方向键缩放
 * - 双击标题栏最大化/还原；localStorage 持久化几何（可选 persistKey）
 * - ESC 关闭经全局 overlayEscStack（后进先关）
 *
 * 用法（通常经由 UFloatingPanel 基件，不必直接使用）：
 *   const panel = usePanelControls({ defaultW: 320, defaultH: 400, onClose: () => (open.value = false) });
 *   <div :style="panel.panelStyle" v-show="open">
 *     <div v-bind="panel.headerBind" @dblclick="panel.toggleMaximize">标题</div>
 *     <div v-bind="panel.resizeHandleBind" />
 *   </div>
 */

export interface PanelControlsOptions {
    defaultW?: number;
    defaultH?: number;
    defaultX?: number;
    defaultY?: number;
    minW?: number;
    minH?: number;
    /** 缩放尺寸上限占视口比例（默认 0.95） */
    maxViewportRatio?: number;
    /** 边缘吸附距离 px；0 = 禁用吸附（默认 24） */
    snapDistance?: number;
    /** localStorage 持久化键；缺省不持久化 */
    persistKey?: string;
    /** 首次打开（无持久化几何）时居中而非落在 (80,80)。默认 false（角落小浮窗如通话 PiP 不居中）。 */
    centerOnFirstOpen?: boolean;
    onClose?: () => void;
}

const GRIP = 48; // 拖拽后至少留在视口内的把手宽度
const PERSIST_PREFIX = 'ufp:';

interface PanelGeometry {
    x: number;
    y: number;
    w: number;
    h: number;
}

function clampGeometry(g: PanelGeometry, minW: number, minH: number, maxWRatio: number, maxHRatio: number): PanelGeometry {
    const vw = typeof window === 'undefined' ? 1280 : window.innerWidth;
    const vh = typeof window === 'undefined' ? 800 : window.innerHeight;
    const w = Math.min(Math.max(g.w, minW), Math.max(minW, Math.floor(vw * maxWRatio)));
    const h = Math.min(Math.max(g.h, minH), Math.max(minH, Math.floor(vh * maxHRatio)));
    return {
        w,
        h,
        x: Math.min(Math.max(g.x, GRIP - w), Math.max(GRIP - w, vw - GRIP)),
        y: Math.min(Math.max(g.y, 0), Math.max(0, vh - GRIP)),
    };
}

function loadPersisted(key: string): Partial<PanelGeometry> | null {
    try {
        const raw = localStorage.getItem(PERSIST_PREFIX + key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (typeof parsed !== 'object' || parsed === null) return null;
        return parsed as Partial<PanelGeometry>;
    } catch {
        return null;
    }
}

export function usePanelControls(opts: PanelControlsOptions = {}) {
    const MIN_W = opts.minW ?? 200;
    const MIN_H = opts.minH ?? 160;
    const MAX_RATIO = opts.maxViewportRatio ?? 0.95;
    const SNAP = opts.snapDistance ?? 24;

    // [2026-10-03] 首开定位：默认 (80,80) 是历史随手值 —— 大面板（如意图启萌）
    // 落在左上角像"挂"在那儿。centerOnFirstOpen=true 且无持久化几何时居中
    // （与 centerOnScreen 同一口径：垂直略偏上 1/2.4，视觉重心稳）。
    const hasPersisted = opts.persistKey ? loadPersisted(opts.persistKey) !== null : false;
    const firstX = hasPersisted ? undefined
        : (opts.centerOnFirstOpen && typeof window !== 'undefined'
            ? Math.max(8, (window.innerWidth - (opts.defaultW ?? 320)) / 2)
            : undefined);
    const firstY = hasPersisted ? undefined
        : (opts.centerOnFirstOpen && typeof window !== 'undefined'
            ? Math.max(8, (window.innerHeight - (opts.defaultH ?? 400)) / 2.4)
            : undefined);

    const initial = clampGeometry(
        {
            x: firstX ?? opts.defaultX ?? 80,
            y: firstY ?? opts.defaultY ?? 80,
            w: opts.defaultW ?? 320,
            h: opts.defaultH ?? 400,
            ...(opts.persistKey ? (loadPersisted(opts.persistKey) ?? {}) : {}),
        },
        MIN_W, MIN_H, MAX_RATIO, MAX_RATIO,
    );

    const pos = reactive<PanelGeometry>({ ...initial });
    const dragging = ref(false);
    const resizing = ref(false);
    const maximized = ref(false);
    let prevGeometry: PanelGeometry | null = null;
    let dragStart = { px: 0, py: 0, x: 0, y: 0 };
    let resizeStart = { px: 0, py: 0, w: 0, h: 0 };

    const panelStyle = computed<StyleValue>(() => ({
        position: 'fixed',
        left: '0',
        top: '0',
        width: `${pos.w}px`,
        height: `${pos.h}px`,
        transform: `translate3d(${pos.x}px, ${pos.y}px, 0)`,
        zIndex: 'var(--z-panel)',
        touchAction: 'none',
        ...(dragging.value || resizing.value ? { willChange: 'width, height, transform' } : {}),
    }));

    /** 释放时贴边吸附：面板边缘贴近视口边缘则贴齐 */
    function snapToEdges() {
        if (SNAP <= 0) return;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        if (pos.x < SNAP) pos.x = 0;
        else if (vw - (pos.x + pos.w) < SNAP) pos.x = Math.max(GRIP - pos.w, vw - pos.w);
        if (pos.y < SNAP) pos.y = 0;
        else if (vh - (pos.y + pos.h) < SNAP) pos.y = Math.max(0, vh - pos.h);
    }

    function onHeaderDown(e: PointerEvent) {
        if (maximized.value) return;
        if (e.button !== 0 && e.pointerType === 'mouse') return;
        const el = e.currentTarget as HTMLElement;
        el.setPointerCapture(e.pointerId);
        dragStart = { px: e.clientX, py: e.clientY, x: pos.x, y: pos.y };
        dragging.value = true;
    }

    function onHeaderMove(e: PointerEvent) {
        if (!dragging.value) return;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        pos.x = Math.min(Math.max(dragStart.x + (e.clientX - dragStart.px), GRIP - pos.w), vw - GRIP);
        pos.y = Math.min(Math.max(dragStart.y + (e.clientY - dragStart.py), 0), vh - GRIP);
    }

    function onHeaderUp() {
        if (!dragging.value) return;
        dragging.value = false;
        snapToEdges();
    }

    /** 键盘微调：方向键移动 8px（Shift=1px）；Ctrl+方向键缩放 */
    function onHeaderKeydown(e: KeyboardEvent) {
        const step = e.shiftKey ? 1 : 8;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        let handled = true;
        switch (e.key) {
            case 'ArrowLeft':
                if (e.ctrlKey) pos.w = Math.max(MIN_W, pos.w - step * 2);
                else pos.x = Math.max(GRIP - pos.w, pos.x - step);
                break;
            case 'ArrowRight':
                if (e.ctrlKey) pos.w = Math.min(Math.floor(vw * MAX_RATIO), pos.w + step * 2);
                else pos.x = Math.min(vw - GRIP, pos.x + step);
                break;
            case 'ArrowUp':
                if (e.ctrlKey) pos.h = Math.max(MIN_H, pos.h - step * 2);
                else pos.y = Math.max(0, pos.y - step);
                break;
            case 'ArrowDown':
                if (e.ctrlKey) pos.h = Math.min(Math.floor(vh * MAX_RATIO), pos.h + step * 2);
                else pos.y = Math.min(vh - GRIP, pos.y + step);
                break;
            default:
                handled = false;
        }
        if (handled) e.preventDefault();
    }

    function toggleMaximize() {
        if (maximized.value) {
            if (prevGeometry) Object.assign(pos, prevGeometry);
            maximized.value = false;
            prevGeometry = null;
        } else {
            prevGeometry = { x: pos.x, y: pos.y, w: pos.w, h: pos.h };
            pos.x = 0;
            pos.y = 0;
            pos.w = window.innerWidth;
            pos.h = window.innerHeight;
            maximized.value = true;
        }
    }

    function onResizeDown(e: PointerEvent) {
        if (maximized.value) return;
        if (e.button !== 0 && e.pointerType === 'mouse') return;
        const el = e.currentTarget as HTMLElement;
        el.setPointerCapture(e.pointerId);
        e.stopPropagation();
        resizeStart = { px: e.clientX, py: e.clientY, w: pos.w, h: pos.h };
        resizing.value = true;
    }

    function onResizeMove(e: PointerEvent) {
        if (!resizing.value) return;
        pos.w = Math.min(Math.max(MIN_W, resizeStart.w + (e.clientX - resizeStart.px)), Math.floor(window.innerWidth * MAX_RATIO));
        pos.h = Math.min(Math.max(MIN_H, resizeStart.h + (e.clientY - resizeStart.py)), Math.floor(window.innerHeight * MAX_RATIO));
    }

    function onResizeUp() {
        resizing.value = false;
    }

    function centerOnScreen() {
        pos.x = Math.max(8, (window.innerWidth - pos.w) / 2);
        pos.y = Math.max(8, (window.innerHeight - pos.h) / 2.4);
        maximized.value = false;
        prevGeometry = null;
    }

    const close = () => { opts.onClose?.(); };

    // 视口变化时重钳制几何：桌面窗口缩放后已打开/持久化恢复的面板不悬在界外
    function onViewportResize() {
        if (maximized.value) {
            pos.w = window.innerWidth;
            pos.h = window.innerHeight;
            return;
        }
        Object.assign(pos, clampGeometry({ x: pos.x, y: pos.y, w: pos.w, h: pos.h }, MIN_W, MIN_H, MAX_RATIO, MAX_RATIO));
    }
    if (typeof window !== 'undefined') window.addEventListener('resize', onViewportResize);

    onBeforeUnmount(() => {
        dragging.value = false;
        resizing.value = false;
        if (typeof window !== 'undefined') window.removeEventListener('resize', onViewportResize);
    });

    // 几何持久化（防抖 200ms）
    if (opts.persistKey) {
        let timer: ReturnType<typeof setTimeout> | undefined;
        watch(
            () => ({ x: pos.x, y: pos.y, w: pos.w, h: pos.h }),
            (g) => {
                clearTimeout(timer);
                timer = setTimeout(() => {
                    try { localStorage.setItem(PERSIST_PREFIX + opts.persistKey, JSON.stringify(g)); } catch { /* 配额/隐私模式静默 */ }
                }, 200);
            },
        );
    }

    return {
        panelStyle,
        pos,
        dragging,
        resizing,
        maximized,
        panelBind: {
            onPointerdown: onHeaderDown,
            onPointermove: onHeaderMove,
            onPointerup: onHeaderUp,
            onPointercancel: onHeaderUp,
            onKeydown: onHeaderKeydown,
            style: { cursor: 'move', userSelect: 'none', touchAction: 'none' } as StyleValue,
        },
        resizeHandleBind: {
            onPointerdown: onResizeDown,
            onPointermove: onResizeMove,
            onPointerup: onResizeUp,
            onPointercancel: onResizeUp,
            style: { position: 'absolute', right: 0, bottom: 0, width: '16px', height: '16px', cursor: 'nwse-resize', touchAction: 'none' } as StyleValue,
        },
        toggleMaximize,
        close,
        centerOnScreen,
        snapToEdges,
    };
}

/** 弹层 ESC 栈便捷注册：入参兼容 getter 函数或 ref；打开入栈、关闭/卸载出栈（幂等）。 */
export function useEscClose(open: (() => boolean) | { value: boolean }, onClose: () => void, label = 'panel') {
    const isOpen = () => (typeof open === 'function' ? open() : open.value);
    let unregister: (() => void) | null = null;
    const sync = () => {
        if (isOpen() && !unregister) unregister = pushOverlay(onClose, label);
        else if (!isOpen() && unregister) { unregister(); unregister = null; }
    };
    watch(isOpen, sync, { immediate: true });
    onBeforeUnmount(() => { unregister?.(); unregister = null; });
}
